import {
	FORM_ATTACHMENT_ACCEPT,
	maxFilesForField,
} from "@multi-tenant-cms/types/forms";
import type { CSSProperties, ReactNode } from "react";

import {
	formFieldOptionDisplayLabel,
	normalizeFormFieldOptionRows,
} from "../ui/lib/formFieldOptions";
import {
	FormLabelWithLinks,
	formLabelPlainText,
} from "../ui/lib/formLabelWithLinks";
import type { FormFieldValueType } from "../ui/types";
import { flatElementStylesToReactCss } from "./flatElementStyles";
import { formFieldSpansFullWidth } from "./formDesign";

/** Theme Manager muted *text* token — `--tt-color-muted` is a surface tint. */
const THEME_MUTED_TEXT_COLOR = "var(--tt-color-muted-foreground, #64748b)";
const DESTRUCTIVE_COLOR =
	"var(--tt-color-destructive, var(--destructive, #b91c1c))";

function invalidControlStyle(hasError: boolean): CSSProperties {
	if (!hasError) {
		return {};
	}
	return {
		borderColor: DESTRUCTIVE_COLOR,
		boxShadow: `0 0 0 1px color-mix(in srgb, ${DESTRUCTIVE_COLOR} 55%, transparent)`,
	};
}

export type FormFieldRenderOptions = {
	fieldError?: string | null;
	onFieldInteraction?: () => void;
};

export type FormFieldProps = {
	__builderFormField?: true;
	__ffName?: string;
	__ffLabel?: string;
	__ffPlaceholder?: string;
	__ffHelp?: string;
	__ffRequired?: boolean;
	__ffHidden?: boolean;
	__ffPresetValue?: string;
	__ffFieldType?: string;
	__ffOptionsJson?: string;
	__ffValidationJson?: string;
	__ffLabelStylesJson?: string;
	__ffInputStylesJson?: string;
};

function inputTypeForFormField(ft: string | undefined): string {
	if (!ft) {
		return "text";
	}
	const t = ft as FormFieldValueType;
	if (
		t === "email" ||
		t === "tel" ||
		t === "number" ||
		t === "date" ||
		t === "file"
	) {
		return t;
	}
	return "text";
}

function safeJsonParseObject(s: string | undefined): Record<string, unknown> {
	if (!s) {
		return {};
	}
	try {
		const v = JSON.parse(s) as unknown;
		return typeof v === "object" && v !== null && !Array.isArray(v)
			? (v as Record<string, unknown>)
			: {};
	} catch {
		return {};
	}
}

function stylesFromJson(s: string | undefined): CSSProperties {
	return flatElementStylesToReactCss(safeJsonParseObject(s));
}

function optionListKey(
	fieldName: string,
	value: string,
	index: number
): string {
	const v = value.trim();
	return v.length > 0 ? `${fieldName}-${v}` : `${fieldName}-opt-${index}`;
}

function optionsFromFieldJson(
	s: string | undefined
): { label: string; value: string }[] {
	if (!s) {
		return [];
	}
	try {
		const p = JSON.parse(s) as unknown;
		return normalizeFormFieldOptionRows(p);
	} catch {
		/* ignore */
	}
	return [];
}

function fieldWrapperDom(
	wrapperDom:
		| { className?: string; id?: string; "data-pb-node"?: string }
		| undefined,
	fieldType: string | undefined
): Record<string, string | undefined> {
	const full = formFieldSpansFullWidth(fieldType);
	return {
		...(wrapperDom ?? {}),
		"data-pb-form-field": "true",
		...(full ? { "data-pb-form-field-full": "true" } : {}),
	};
}

function fieldErrorMessage(
	rootId: string,
	message: string | null | undefined
): ReactNode {
	if (!message?.trim()) {
		return null;
	}
	const errorId = `${rootId}-error`;
	return (
		<span
			className="pb-form-field-error"
			id={errorId}
			role="alert"
			style={{
				color: DESTRUCTIVE_COLOR,
				fontSize: 12,
				margin: 0,
			}}
		>
			{message}
		</span>
	);
}

function describedByIds(
	helpId: string | undefined,
	hasError: boolean,
	rootId: string
): string | undefined {
	const ids = [helpId, hasError ? `${rootId}-error` : undefined].filter(
		Boolean
	);
	return ids.length > 0 ? ids.join(" ") : undefined;
}

function renderFormFieldPublic(
	id: string,
	props: FormFieldProps,
	style: CSSProperties,
	wrapperDom?: { className?: string; id?: string; "data-pb-node"?: string },
	options?: FormFieldRenderOptions
): ReactNode {
	const lStyle = stylesFromJson(props.__ffLabelStylesJson);
	const iStyle = stylesFromJson(props.__ffInputStylesJson);
	const name = (props.__ffName || "field").trim() || "field";
	const rootId = wrapperDom?.id?.trim() || id;
	const helpId = props.__ffHelp ? `${rootId}-help` : undefined;
	const inputId = `${rootId}-input`;
	const fieldType = props.__ffFieldType;
	const fieldError = options?.fieldError ?? null;
	const onFieldInteraction = options?.onFieldInteraction;
	const invalidStyle = invalidControlStyle(!!fieldError);
	const v = safeJsonParseObject(props.__ffValidationJson) as unknown as {
		pattern?: string;
		min?: number;
		max?: number;
		maxLength?: number;
	};

	if (props.__ffHidden) {
		return (
			<input
				name={name}
				style={{ ...iStyle, display: "none" }}
				type="hidden"
				value={props.__ffPresetValue ?? ""}
			/>
		);
	}
	if (fieldType === "checkbox") {
		return (
			<div
				{...fieldWrapperDom(wrapperDom, fieldType)}
				className={fieldError ? "pb-form-field-invalid" : undefined}
				id={rootId}
				style={{
					...style,
					display: "flex",
					flexDirection: "column",
					gap: 6,
				}}
			>
				<div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
					<input
						aria-describedby={describedByIds(helpId, !!fieldError, rootId)}
						aria-invalid={fieldError ? true : undefined}
						id={inputId}
						name={name}
						onChange={onFieldInteraction}
						required={props.__ffRequired}
						style={{ ...iStyle, ...invalidStyle }}
						type="checkbox"
						value="true"
					/>
					<label htmlFor={inputId} style={lStyle}>
						<FormLabelWithLinks text={props.__ffLabel} />
					</label>
				</div>
				{props.__ffHelp ? (
					<span
						className="pb-form-field-help"
						id={helpId}
						style={{
							fontSize: 12,
							color: THEME_MUTED_TEXT_COLOR,
							margin: 0,
						}}
					>
						{props.__ffHelp}
					</span>
				) : null}
				{fieldErrorMessage(rootId, fieldError)}
			</div>
		);
	}

	if (fieldType === "textarea") {
		return (
			<div
				{...fieldWrapperDom(wrapperDom, fieldType)}
				className={fieldError ? "pb-form-field-invalid" : undefined}
				id={rootId}
				style={{ ...style, display: "flex", flexDirection: "column", gap: 6 }}
			>
				<label htmlFor={inputId} style={lStyle}>
					<FormLabelWithLinks text={props.__ffLabel} />
				</label>
				<textarea
					aria-describedby={describedByIds(helpId, !!fieldError, rootId)}
					aria-invalid={fieldError ? true : undefined}
					id={inputId}
					name={name}
					onChange={onFieldInteraction}
					placeholder={props.__ffPlaceholder}
					required={props.__ffRequired}
					rows={4}
					style={{ ...iStyle, ...invalidStyle }}
				/>
				{props.__ffHelp ? (
					<span
						className="pb-form-field-help"
						id={helpId}
						style={{
							fontSize: 12,
							color: THEME_MUTED_TEXT_COLOR,
							margin: 0,
						}}
					>
						{props.__ffHelp}
					</span>
				) : null}
				{fieldErrorMessage(rootId, fieldError)}
			</div>
		);
	}
	if (fieldType === "select") {
		const opts = optionsFromFieldJson(props.__ffOptionsJson);
		return (
			<div
				{...fieldWrapperDom(wrapperDom, fieldType)}
				className={fieldError ? "pb-form-field-invalid" : undefined}
				id={rootId}
				style={{ ...style, display: "flex", flexDirection: "column", gap: 6 }}
			>
				<label htmlFor={inputId} style={lStyle}>
					<FormLabelWithLinks text={props.__ffLabel} />
				</label>
				<select
					aria-describedby={describedByIds(helpId, !!fieldError, rootId)}
					aria-invalid={fieldError ? true : undefined}
					id={inputId}
					name={name}
					onChange={onFieldInteraction}
					required={props.__ffRequired}
					style={{ ...iStyle, ...invalidStyle }}
				>
					<option key={`${inputId}-placeholder`} value="">
						{props.__ffPlaceholder?.trim() || "Choose..."}
					</option>
					{opts.map((o, index) => (
						<option key={optionListKey(name, o.value, index)} value={o.value}>
							{formLabelPlainText(formFieldOptionDisplayLabel(o))}
						</option>
					))}
				</select>
				{props.__ffHelp ? (
					<span
						className="pb-form-field-help"
						id={helpId}
						style={{
							fontSize: 12,
							color: THEME_MUTED_TEXT_COLOR,
							margin: 0,
						}}
					>
						{props.__ffHelp}
					</span>
				) : null}
				{fieldErrorMessage(rootId, fieldError)}
			</div>
		);
	}
	if (
		fieldType === "radio" &&
		optionsFromFieldJson(props.__ffOptionsJson).length > 0
	) {
		const opts = optionsFromFieldJson(props.__ffOptionsJson);
		return (
			<div
				{...fieldWrapperDom(wrapperDom, fieldType)}
				aria-describedby={describedByIds(helpId, !!fieldError, rootId)}
				aria-invalid={fieldError ? true : undefined}
				className={fieldError ? "pb-form-field-invalid" : undefined}
				id={rootId}
				role="radiogroup"
				style={{ ...style, display: "flex", flexDirection: "column", gap: 8 }}
			>
				{props.__ffLabel ? (
					<span key={`${rootId}-legend`} style={lStyle}>
						<FormLabelWithLinks text={props.__ffLabel} />
					</span>
				) : null}
				{opts.map((o, index) => (
					<label
						key={optionListKey(name, o.value, index)}
						style={{ display: "flex", alignItems: "center", gap: 8 }}
					>
						<input
							name={name}
							onChange={onFieldInteraction}
							required={props.__ffRequired}
							type="radio"
							value={o.value}
						/>
						<span>
							<FormLabelWithLinks text={formFieldOptionDisplayLabel(o)} />
						</span>
					</label>
				))}
				{props.__ffHelp ? (
					<span
						className="pb-form-field-help"
						id={helpId}
						key={helpId}
						style={{
							fontSize: 12,
							color: THEME_MUTED_TEXT_COLOR,
							margin: 0,
						}}
					>
						{props.__ffHelp}
					</span>
				) : null}
				{fieldErrorMessage(rootId, fieldError)}
			</div>
		);
	}
	const it = inputTypeForFormField(fieldType);
	const fileAttrs =
		it === "file"
			? {
					accept: FORM_ATTACHMENT_ACCEPT,
					multiple: maxFilesForField(v.max) > 1,
				}
			: {
					max: v.max,
					maxLength: v.maxLength,
					min: v.min,
					pattern: v.pattern as string | undefined,
					placeholder: props.__ffPlaceholder,
				};
	return (
		<div
			{...fieldWrapperDom(wrapperDom, fieldType)}
			className={fieldError ? "pb-form-field-invalid" : undefined}
			id={rootId}
			style={{ ...style, display: "flex", flexDirection: "column", gap: 6 }}
		>
			<label htmlFor={inputId} style={lStyle}>
				<FormLabelWithLinks text={props.__ffLabel} />
			</label>
			<input
				aria-describedby={describedByIds(helpId, !!fieldError, rootId)}
				aria-invalid={fieldError ? true : undefined}
				id={inputId}
				{...fileAttrs}
				name={name}
				onChange={onFieldInteraction}
				required={props.__ffRequired}
				style={{ ...iStyle, ...invalidStyle }}
				type={it}
			/>
			{props.__ffHelp ? (
				<span
					className="pb-form-field-help"
					id={helpId}
					style={{
						fontSize: 12,
						color: THEME_MUTED_TEXT_COLOR,
						margin: 0,
					}}
				>
					{props.__ffHelp}
				</span>
			) : null}
			{fieldErrorMessage(rootId, fieldError)}
		</div>
	);
}

/** Public canvas / region runtime — render a serialized `form_field` container. */
export function renderFormFieldFromContainerProps(
	id: string,
	props: FormFieldProps,
	style: CSSProperties,
	wrapperDom?: { className?: string; id?: string; "data-pb-node"?: string },
	options?: FormFieldRenderOptions
): ReactNode {
	return renderFormFieldPublic(id, props, style, wrapperDom, options);
}

export function renderFormSubmitPublic(
	id: string,
	props: { __fsContent?: string },
	style: CSSProperties,
	dom?: { className?: string; id?: string; "data-pb-node"?: string }
): ReactNode {
	const rootId = dom?.id?.trim() || id;
	return (
		<button
			{...(dom ?? {})}
			data-pb-form-submit="true"
			id={rootId}
			style={{ ...style, cursor: "pointer" }}
			type="submit"
		>
			{props.__fsContent || "Submit"}
		</button>
	);
}

export function renderFormConsentPublic(
	id: string,
	props: {
		__fcName?: string;
		__fcLabel?: string;
		__fcRequired?: boolean;
		__fcLabelStylesJson?: string;
		__fcInputStylesJson?: string;
	},
	style: CSSProperties,
	dom?: { className?: string; id?: string; "data-pb-node"?: string },
	options?: FormFieldRenderOptions
) {
	const rootId = dom?.id?.trim() || id;
	const consentId = `${rootId}-consent`;
	const fieldError = options?.fieldError ?? null;
	const onFieldInteraction = options?.onFieldInteraction;
	const name = (props.__fcName || "consent").trim() || "consent";
	const labelStyle = stylesFromJson(props.__fcLabelStylesJson);
	const inputStyle = stylesFromJson(props.__fcInputStylesJson);
	const consentClassName = [
		dom?.className,
		fieldError ? "pb-form-field-invalid" : undefined,
	]
		.filter(Boolean)
		.join(" ");
	return (
		<div
			{...(dom ?? {})}
			className={consentClassName || undefined}
			data-pb-form-consent="true"
			id={rootId}
			style={{
				...style,
				display: "flex",
				flexDirection: "column",
				gap: 6,
			}}
		>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
				<input
					aria-describedby={fieldError ? `${rootId}-error` : undefined}
					aria-invalid={fieldError ? true : undefined}
					id={consentId}
					name={name}
					onChange={onFieldInteraction}
					required={props.__fcRequired}
					style={{ ...inputStyle, ...invalidControlStyle(!!fieldError) }}
					type="checkbox"
					value="true"
				/>
				<label htmlFor={consentId} style={labelStyle}>
					<FormLabelWithLinks text={props.__fcLabel} />
				</label>
			</div>
			{fieldErrorMessage(rootId, fieldError)}
		</div>
	);
}
